'use client';

import { useState, useCallback, useEffect } from 'react';
import Image from 'next/image';
import { PROJECT_NAME, PROJECT_DOMAIN } from '@shared/constants';
import { BoardMemberCard } from '../board';
import { BOARD_MEMBERS } from '../board/constants/BOARD_MEMBERS';
import type { BoardMember } from '../board/interfaces';
import { getInitials } from '../board/utils/getInitials';

/**
 * @group Public
 * @summary Board of Directors page component
 *
 * Displays the board of directors with professional profiles,
 * headshots, and biographical information.
 * Clicking a card opens a detail panel above the grid.
 * The card grid never changes layout — only the detail panel
 * opens/closes with a smooth CSS height transition.
 */
export function BoardOfDirectorsPage() {
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const handleExpand = useCallback((memberId: string) => {
    setExpandedMemberId((prev) => {
      const isCurrentlyExpanded = prev !== null;
      const isClickingSameMember = prev === memberId;
      const willExpand = !isClickingSameMember;
      
      // Scroll to top if clicking a board member when none is already expanded
      if (!isCurrentlyExpanded && willExpand) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      return isClickingSameMember ? null : memberId;
    });
  }, []);

  const handleClose = useCallback(() => {
    setExpandedMemberId(null);
  }, []);

  const expandedMember = BOARD_MEMBERS.find((m) => m.id === expandedMemberId) ?? null;
  const hasExpanded = expandedMember !== null;

  // Structured data for SEO (JSON-LD)
  const baseUrl = `https://www.${PROJECT_DOMAIN}`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `Board of Directors | ${PROJECT_NAME}`,
    description: `Meet the leadership team guiding ${PROJECT_NAME}'s mission to bring quality healthcare to every South African.`,
    url: `${baseUrl}/board`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: BOARD_MEMBERS.map((member, index) => ({
        '@type': 'Person',
        position: index + 1,
        name: member.name,
        jobTitle: member.job_title,
        description: member.biography.intro,
        image: `${baseUrl}${member.imageUrl}`,
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-50 to-white pt-4 sm:pt-5 pb-4 sm:pb-5">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 text-center">
              Board of Directors
            </h1>
          </div>
        </section>

        {/* Board Members Section */}
        <section className="pt-0 pb-12 sm:pb-16 lg:pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {hasExpanded ? (
              /* Expanded layout: Detail panel with picture thumbnails */
              <>
                {/* Desktop: Split layout with detail panel on left, pictures on right */}
                <div className="hidden lg:grid lg:grid-cols-[1fr_120px] gap-6 items-start">
                  {/* Detail Panel */}
                  <div className="transition-opacity duration-300 ease-in-out">
                    {expandedMember && (
                      <DetailPanel member={expandedMember} onClose={handleClose} isMobile={false} />
                    )}
                  </div>

                  {/* Board Member Pictures on Right Side */}
                  <div className="flex flex-col gap-2 mt-0">
                    {BOARD_MEMBERS.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => handleExpand(member.id)}
                        className={`
                          w-full rounded-lg border-2 transition-all duration-300 aspect-square
                          ${member.id === expandedMemberId
                            ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-500'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }
                        `}
                        aria-expanded={member.id === expandedMemberId}
                        aria-label={`View ${member.name}`}
                      >
                        <MemberPictureOnly
                          member={member}
                          isExpanded={member.id === expandedMemberId}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mobile: Detail panel on top, horizontal scrollable pictures below */}
                <div className="lg:hidden space-y-4">
                  {/* Detail Panel */}
                  <div className="transition-opacity duration-300 ease-in-out">
                    {expandedMember && (
                      <DetailPanel member={expandedMember} onClose={handleClose} isMobile={true} />
                    )}
                  </div>

                  {/* Board Member Pictures - Horizontal scrollable row */}
                  <div className="flex flex-row gap-2 overflow-x-auto pb-2 -mx-4 px-4">
                    {BOARD_MEMBERS.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => handleExpand(member.id)}
                        className={`
                          flex-shrink-0 w-20 h-20 rounded-lg border-2 transition-all duration-300
                          ${member.id === expandedMemberId
                            ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-500'
                            : 'border-gray-200 bg-white'
                          }
                        `}
                        aria-expanded={member.id === expandedMemberId}
                        aria-label={`View ${member.name}`}
                      >
                        <MemberPictureOnly
                          member={member}
                          isExpanded={member.id === expandedMemberId}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              /* Default grid layout when nothing is expanded */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {BOARD_MEMBERS.map((member) => (
                  <BoardMemberCard
                    key={member.id}
                    member={member}
                    isExpanded={member.id === expandedMemberId}
                    hasAnyExpanded={false}
                    onExpand={() => handleExpand(member.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}

/** Component with picture only for right sidebar when a member is expanded (desktop only) */
function MemberPictureOnly({
  member,
  isExpanded,
}: {
  member: BoardMember;
  isExpanded: boolean;
}) {
  const [imageError, setImageError] = useState(false);
  const initialsResult = getInitials(member);
  const initials = initialsResult.match({
    ok: (value) => value,
    err: () => '?',
  });

  // Reset image error state when member changes
  useEffect(() => {
    setImageError(false);
  }, [member.id]);

  return (
    <div className="w-full h-full rounded-lg relative bg-gradient-to-br from-emerald-100 to-emerald-200 overflow-hidden">
      {!imageError ? (
        <Image
          src={member.imageUrl}
          alt={`${member.name} - ${member.role}`}
          fill
          className="object-cover object-top"
          sizes="120px"
          quality={85}
          priority={false}
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-lg font-bold text-emerald-700 opacity-80">
            {initials}
          </div>
        </div>
      )}
    </div>
  );
}

/** Expanded detail panel for the selected board member */
function DetailPanel({ 
  member, 
  onClose, 
  isMobile = false 
}: { 
  member: BoardMember; 
  onClose: () => void;
  isMobile?: boolean;
}) {
  const [imageError, setImageError] = useState(false);
  const initialsResult = getInitials(member);
  const initials = initialsResult.match({
    ok: (value) => value,
    err: () => '?',
  });

  // Reset image error state when member changes
  useEffect(() => {
    setImageError(false);
  }, [member.id]);

  return (
    <div
      key={member.id}
      className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in"
    >
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'md:grid-cols-[300px_1fr]'} gap-6 ${isMobile ? 'p-4 sm:p-6' : 'p-6 md:p-8'}`}>
        {/* Headshot Image */}
        <div className={`${isMobile ? 'aspect-[3/4] max-w-xs mx-auto w-full' : 'aspect-[3/4]'} relative bg-gradient-to-br from-emerald-100 to-emerald-200 overflow-hidden rounded-lg ${isMobile ? '' : 'max-h-[500px]'}`}>
          {!imageError ? (
            <Image
              src={member.imageUrl}
              alt={`${member.name} - ${member.role}`}
              fill
              className="object-cover object-top"
              sizes={isMobile ? "(max-width: 640px) 100vw, 300px" : "300px"}
              quality={85}
              priority={false}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className={`font-bold text-emerald-700 opacity-80 ${isMobile ? 'text-5xl sm:text-6xl' : 'text-7xl'}`}>
                {initials}
              </div>
            </div>
          )}
        </div>

        {/* Member Info */}
        <div className="flex flex-col justify-start">
          <div className={`flex items-start justify-between ${isMobile ? 'mb-3' : 'mb-2'}`}>
            <div className="flex-1">
              <h3 className={`font-bold text-gray-900 ${isMobile ? 'text-xl sm:text-2xl' : 'text-2xl lg:text-3xl'}`}>
                {member.name}
              </h3>
              <p className={`text-emerald-600 font-semibold mt-1 ${isMobile ? 'text-sm sm:text-base' : 'text-base lg:text-lg'}`}>
                {member.job_title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100 flex-shrink-0 ml-4"
              aria-label="Close expanded view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Biography */}
          <div className={`text-gray-700 leading-relaxed space-y-3 mt-4 overflow-y-auto max-h-[500px] pr-2`}>
            <p className={`font-medium ${isMobile ? 'text-base' : 'text-base lg:text-lg'}`}>
              {member.biography.intro}
            </p>
            {member.biography.paragraphs.map((paragraph, index) => (
              <p key={index} className={`text-gray-600 leading-relaxed ${isMobile ? 'text-sm' : 'text-sm lg:text-base'}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
